import { Injectable } from '@angular/core';
import { AppVersions, ScreenAsleepStatusEnum, ScreenServicesStateData, ServiceStatus, ServiceVersionMismatch } from '@app/admin/screen/screen.model';
import { DeviceServiceState } from '@app/admin/screen/model/device.model';
import { DeviceServiceStatusEnum } from '@app/shared/models/enums/deviceServiceStatusEnum';
import { TranslateConfigService } from '../translate-config.service';
import { DatePipe } from '@angular/common';
import { DateTime } from 'luxon';
import { DeviceType } from '@app/shared/models/enums/deviceType.enum';

@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  inactiveServices: ServiceStatus[];
  baseToolTipMessage: string;
  constructor(
    public translateConfigService: TranslateConfigService,
    private datePipe: DatePipe
  ) { }

  serviceStatus(
    screenServiceStateData: ScreenServicesStateData,
    deviceType?: DeviceType
  ): { color: string; label: string; tooltip?: string; ping?: boolean } {
    let tooltipMsg = '';
    let tooltip: { color: string; label: string; tooltip?: string; ping?: boolean } = {
      color: 'bg-brand-red text-white',
      label: 'global.offline',
    };

    if (screenServiceStateData?.screenServiceStatus === DeviceServiceStatusEnum.Red) {
      tooltipMsg = this.getOfflineTooltipMessage(screenServiceStateData?.lastAliveDate);
      return { color: 'bg-brand-red text-white', label: 'global.offline', tooltip: tooltipMsg };
    }

    if (screenServiceStateData?.screenServiceStatus === DeviceServiceStatusEnum.Black) {
      tooltipMsg = this.translateConfigService.instant('screens.sleepModeTooltip');
      tooltip = { color: 'bg-gray-800 text-white', label: 'global.sleep', tooltip: tooltipMsg };
    }

    if (screenServiceStateData?.screenServiceStatus === DeviceServiceStatusEnum.Yellow) {
      tooltipMsg = this.getServicesTooltipMessage(screenServiceStateData);
      tooltip = { color: 'bg-yellow-500 text-black', label: 'global.warning', tooltip: tooltipMsg };

      if (deviceType !== DeviceType.ScreenOnly && screenServiceStateData?.aiFps?.isFpsBelowThreshold) {
        tooltipMsg += tooltipMsg ? '\n\n' : '';
        tooltipMsg += this.translateConfigService.instantWithParams('screens.yellowLightFPSTooltip');

        tooltip.tooltip = tooltipMsg;
      }
    }

    if (screenServiceStateData?.screenServiceStatus === DeviceServiceStatusEnum.Green) {
      tooltip = { color: 'bg-brand-green text-white', label: 'global.online' };
    }

    if (screenServiceStateData?.screenServiceStatus === DeviceServiceStatusEnum.Violet) {
      tooltipMsg = this.translateConfigService.instant('screens.unknownDeviceTooltip');
      return { color: 'text-white bg-violet-800', label: 'global.unknown', tooltip: tooltipMsg, ping: true };
    }

    if (this.hasVersionMismatch(screenServiceStateData?.appVersions, screenServiceStateData?.currentDeviceAppVersions, deviceType)) {
      const mismatchMsg = this.getVersionMismatchTooltipMsg(
        screenServiceStateData.appVersions,
        screenServiceStateData.currentDeviceAppVersions,
        deviceType
      );
      if (mismatchMsg) {
        tooltipMsg = tooltipMsg ? `${tooltipMsg}\n\n${mismatchMsg}` : mismatchMsg;
        tooltip = { color: 'bg-yellow-500 text-black', label: 'global.warning', tooltip: tooltipMsg };
      }
    }

    if (deviceType === DeviceType.ScreenOnly && this.hasAiForScreenOnlyDevice(screenServiceStateData)) {
      const configMsg = this.translateConfigService.instant('screens.yellowLightScreenOnlyHasAiConfig');
      tooltipMsg = tooltipMsg ? `${tooltipMsg}\n\n${configMsg}` : configMsg;
      tooltip = { color: 'bg-yellow-500 text-black', label: 'global.warning', tooltip: tooltipMsg };
    }

    if (screenServiceStateData == null || screenServiceStateData?.screenServiceStatus == DeviceServiceStatusEnum.Blue) {
      tooltipMsg = this.translateConfigService.instant('screens.newDeviceTooltip');
      tooltip = { color: 'bg-brand-chathams-blue text-white', label: 'global.new', tooltip: tooltipMsg };
    }

    return tooltip;
  }

  serviceStatusKeys(state: ScreenServicesStateData | DeviceServiceState, deviceType?: DeviceType): string[] {
    if (state == null || state.screenServiceStatus === DeviceServiceStatusEnum.Blue) {
      return [];
    }

    if (state.screenServiceStatus === DeviceServiceStatusEnum.Violet) {
      return ['deviceReports.table.serviceStatusValues.unknown'];
    }

    if (state.screenServiceStatus === DeviceServiceStatusEnum.Red) {
      return [];
    }

    const keys: string[] = [];

    if (state.screenServiceStatus === DeviceServiceStatusEnum.Yellow) {
      const aiServiceKey = 'deviceReports.table.serviceStatusValues.bizlabAiService';
      const isFpsBelowThreshold = deviceType !== DeviceType.ScreenOnly && state.aiFps?.isFpsBelowThreshold === true;

      keys.push(
        ...this.getInactiveServices(state)
          .map(service => service.statusKey)
          .filter(key => !(isFpsBelowThreshold && key === aiServiceKey))
      );

      if (isFpsBelowThreshold) {
        keys.push('deviceReports.table.serviceStatusValues.bizlabAiFps');
      }
    }

    if (this.hasVersionMismatch(state.appVersions, state.currentDeviceAppVersions, deviceType)) {
      keys.push(...this.getVersionMismatchDetails(state.appVersions, state.currentDeviceAppVersions, deviceType));
    }

    if (deviceType === DeviceType.ScreenOnly && this.hasAiForScreenOnlyDevice(state)) {
      keys.push('screens.yellowLightScreenOnlyHasAiConfig');
    }

    return keys;
  }

  private getVersionMismatchDetails(appVersions: AppVersions, currentDeviceAppVersions: AppVersions, deviceType?: DeviceType): string[] {
    return this.getMismatchedServices(appVersions, currentDeviceAppVersions, deviceType).map(service =>
      this.translateConfigService.instantWithParams('screens.yellowLightVersionMismatchDetail', {
        service: service.name,
        deviceVersion: service.deviceVersion,
        flowVersion: service.flowVersion,
      })
    );
  }

  private getServicesTooltipMessage(screenServicesStateData: ScreenServicesStateData): string {
    let tooltipMsg = '';
    this.inactiveServices = this.getInactiveServices(screenServicesStateData);

    if (this.inactiveServices.length > 0) {
      tooltipMsg = this.getYellowTooltipMsg(this.inactiveServices, screenServicesStateData.screenAsleepStatus);
    }
    return tooltipMsg;
  }

  private hasAiForScreenOnlyDevice(screenServiceStateData: ScreenServicesStateData | DeviceServiceState): boolean {
    return (
      !!screenServiceStateData?.aiServiceIsAlive ||
      Object.values(screenServiceStateData?.aiFps ?? {}).some(x => x) ||
      !!screenServiceStateData?.appVersions?.aiVersion ||
      !!screenServiceStateData?.currentDeviceAppVersions?.aiVersion
    );
  }

  private getInactiveServices(data: ScreenServicesStateData | DeviceServiceState): ServiceStatus[] {
    const services = this.mapServicesWithLastDate(data);

    if (data.screenAsleepStatus === ScreenAsleepStatusEnum.Asleep) {
      return services.filter(service => service.name !== 'Redis');
    }
    return services.filter(service => service.isAlive === false);
  }

  private mapServicesWithLastDate(data: ScreenServicesStateData | DeviceServiceState): ServiceStatus[] {
    const services: ServiceStatus[] = [
      {
        name: 'Redis',
        isAlive: data.redisIsAlive,
        lastAlive: data.lastAliveDate,
        translationKey: 'screens.redisLastAlive',
        statusKey: 'deviceReports.table.serviceStatusValues.offlineCaching',
      },
      {
        name: 'Advertiser Service',
        isAlive: data.advertiserServiceIsAlive,
        lastAlive: data.lastAliveDate,
        translationKey: 'screens.advertiserLastAlive',
        statusKey: 'deviceReports.table.serviceStatusValues.bizlabAdvertiser',
      },
      {
        name: 'AI Service',
        isAlive: data.aiServiceIsAlive,
        lastAlive: data.lastAliveDate,
        translationKey: 'screens.aiLastAlive',
        statusKey: 'deviceReports.table.serviceStatusValues.bizlabAiService',
      },
    ];
    return services;
  }

  private getYellowTooltipMsg(inactiveServices: ServiceStatus[], deviceSleepMode: ScreenAsleepStatusEnum): string {
    const tooltipParts: string[] = [];

    if (deviceSleepMode === ScreenAsleepStatusEnum.Asleep) {
      this.baseToolTipMessage = this.translateConfigService.instant('screens.yellowLightNotValidSleepTooltip');
    } else {
      this.baseToolTipMessage = this.translateConfigService.instant('screens.yellowLightTooltip');
    }

    tooltipParts.push(this.baseToolTipMessage);

    inactiveServices.forEach(item => {
      if (item.lastAlive) {
        const formattedDate = this.getFormattedDate(item.lastAlive);
        tooltipParts.push(this.translateConfigService.instantWithParams(item.translationKey, { date: formattedDate }));
      }
    });

    return tooltipParts.join('\n');
  }

  private getVersionMismatchTooltipMsg(appVersions: AppVersions, currentDeviceAppVersions: AppVersions, deviceType?: DeviceType): string {
    const header = this.translateConfigService.instant('screens.yellowLightVersionMismatch');
    const sectionHeader = this.translateConfigService.instant('screens.yellowLightVersionMismatchHeader');

    const detailLines = this.getMismatchedServices(appVersions, currentDeviceAppVersions, deviceType).map(s =>
      this.translateConfigService.instantWithParams('screens.yellowLightVersionMismatchDetail', {
        service: s.name,
        deviceVersion: s.deviceVersion ?? '/',
        flowVersion: s.flowVersion ?? '/',
      })
    );

    if (!detailLines.length) return '';
    return `${header}\n\n${sectionHeader}\n${detailLines.join('\n')}`;
  }

  private getMismatchedServices(
    appVersions: AppVersions,
    currentDeviceAppVersions: AppVersions,
    deviceType?: DeviceType
  ): ServiceVersionMismatch[] {
    const services: ServiceVersionMismatch[] = [
      {
        name: 'BizlabAdvertiser',
        deviceVersion: this.extractVersionFromVersionName(appVersions.advertiserVersion),
        flowVersion: this.extractVersionFromVersionName(currentDeviceAppVersions.advertiserVersion),
      },
      {
        name: 'BizlabAI',
        deviceVersion: this.extractVersionFromVersionName(appVersions.aiVersion),
        flowVersion: this.extractVersionFromVersionName(currentDeviceAppVersions.aiVersion),
      },
      {
        name: 'BizlabManager',
        deviceVersion: this.extractVersionFromVersionName(appVersions.managerVersion),
        flowVersion: this.extractVersionFromVersionName(currentDeviceAppVersions.managerVersion),
      },
    ];
    // Screen-only devices should not have AI; the AI-config warning is surfaced separately,
    // so exclude the AI service from the version-mismatch check to avoid a duplicate warning.
    const relevantServices =
      deviceType === DeviceType.ScreenOnly ? services.filter(s => s.name !== 'BizlabAI') : services;
    return relevantServices.filter(s => s.deviceVersion !== s.flowVersion);
  }

  private hasVersionMismatch(appVersions: AppVersions, currentDeviceAppVersions: AppVersions, deviceType?: DeviceType): boolean {
    if (!appVersions || !currentDeviceAppVersions) {
      return false;
    }
    return this.getMismatchedServices(appVersions, currentDeviceAppVersions, deviceType).length > 0;
  }

  private getOfflineTooltipMessage(screenLastAliveDate: string): string {
    if (screenLastAliveDate) {
      const formattedDate = this.getFormattedDate(screenLastAliveDate);
      return this.translateConfigService.instantWithParams('screens.errorTooltip', { screenLastAliveDate: formattedDate });
    }
    return this.translateConfigService.instantWithParams('screens.errorTooltip', { screenLastAliveDate: '/' });
  }

  private getFormattedDate(date: string): string {
    const formatedDate = DateTime.fromISO(date).toLocal().toFormat('yyyy-MM-dd HH:mm:ss');
    return formatedDate;
  }

  private extractVersionFromVersionName(versionName: string): string {
    if (!versionName) {
      return '/';
    }
    const first = versionName.indexOf('_');
    const last = versionName.lastIndexOf('_');
    if (first === -1 || first === last) {
      return versionName;
    }
    return versionName.substring(first + 1, last);
  }
}
